/**
 * My Profile — available to ALL roles (incl. PATIENT / ORGANIZATION).
 * "Benevita Polar" styled: gradient header banner, section cards.
 *
 * PATIENT role: full auditable patient record (flags banner, medical profile,
 * emergency contact, contacts, preferences, care summary, consents) via the
 * self-scoped /patients/{party_id}/* endpoints.
 * NURSE role: professional profile (license, specialisations, rating) +
 * availability & coverage tab — self-editable via /nurses/{id}/profile.
 * DOCTOR role: professional profile (license, specialisation, affiliation)
 * — self-editable via /doctors/{id}/profile.
 * ADMIN / BILLING / QA: overview tab (personal info + contacts + role purpose).
 * ORGANIZATION: organisation card + contacts.
 * Everyone: redesigned Account & security tab (password card w/ strength
 * meter, active session card, security status card).
 *
 * NOTE: all sub-components are defined at module top level (never inside the
 * page component) — inline definitions remount inputs on every keystroke.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI, patientAPI, nurseAPI, doctorAPI, partyAPI } from '../../services/api';
import { Badge, ErrorBanner, SuccessBanner, Spinner, TabBar } from '../../components/ss';
import { COLOR, statusBadge } from '../../components/ss/tokens';
import FamilyAccess from './FamilyAccess';

// ── Polar kit (local) ────────────────────────────────────────────────────────
const BP = {
  primary: COLOR.primary, navy: COLOR.navy, danger: COLOR.red, muted: '#8b7681',
  border: '#ECE3E7', soft: '#f7f3f4',
  font: "'Inter', -apple-system, 'Segoe UI', sans-serif",
  grad: 'linear-gradient(135deg, #2B1B22, #7b1e3c)',
  gradBtn: 'linear-gradient(135deg, #7b1e3c, #94335A)',
};

const bpInitials = (name) =>
  (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

const BPAvatar = ({ name, size = 56 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%',
    background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.3)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.round(size * 0.36), fontWeight: 700, fontFamily: BP.font, flexShrink: 0 }}>
    {bpInitials(name)}
  </div>
);

const BPSection = ({ icon, title, children, action }) => (
  <div style={{ background: '#fff', border: `1px solid ${BP.border}`, borderRadius: 14,
    padding: 18, marginBottom: 14, fontFamily: BP.font,
    animation: 'bpFadeUp .35s ease both' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: BP.navy,
        display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>{title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const bpInputBase = (focus) => ({
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13,
  fontFamily: BP.font, color: BP.navy, background: '#fff', borderRadius: 10,
  border: `1.5px solid ${focus ? BP.primary : '#ECE3E7'}`, outline: 'none',
  boxShadow: focus ? '0 0 0 4px rgba(123,30,60,.18)' : 'none',
  transition: 'border-color .15s, box-shadow .15s',
});

function BPTextInput({ value, onChange, placeholder, type = 'text', suffix }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ ...bpInputBase(focus), paddingRight: suffix ? 40 : 12 }} />
      {suffix && (
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          fontSize: 12, fontWeight: 700, color: BP.muted, fontFamily: BP.font, pointerEvents: 'none' }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

function BPSelect({ value, onChange, children }) {
  const [focus, setFocus] = useState(false);
  return (
    <select value={value} onChange={onChange}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...bpInputBase(focus), cursor: 'pointer' }}>
      {children}
    </select>
  );
}

function BPTextarea({ value, onChange, rows = 3, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <textarea value={value} onChange={onChange} rows={rows} placeholder={placeholder}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...bpInputBase(focus), resize: 'vertical' }} />
  );
}

function BPPasswordInput({ value, onChange, autoComplete }) {
  const [focus, setFocus] = useState(false);
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        autoComplete={autoComplete}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ ...bpInputBase(focus), paddingRight: 56 }} />
      <button type="button" onClick={() => setShow(s => !s)} tabIndex={-1}
        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
          fontWeight: 700, color: BP.muted, fontFamily: BP.font }}>
        {show ? 'HIDE' : 'SHOW'}
      </button>
    </div>
  );
}

const BPField = ({ label, children }) => (
  <label style={{ display: 'block', fontFamily: BP.font, marginBottom: 12 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 5 }}>
      {label}
    </span>
    {children}
  </label>
);

const BPButton = ({ children, disabled, ghostDanger, ghost, small, onClick, type = 'button' }) => (
  <button type={type} disabled={disabled} onClick={onClick} style={{
    padding: small ? '7px 14px' : '10px 22px', borderRadius: 10,
    fontSize: small ? 12.5 : 13.5, fontWeight: 700, fontFamily: BP.font,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, transition: 'all .15s',
    ...(ghostDanger
      ? { background: 'transparent', border: `1.5px solid ${BP.danger}`, color: BP.danger }
      : ghost
        ? { background: 'transparent', border: `1.5px solid ${BP.primary}`, color: BP.primary }
        : { background: BP.gradBtn, border: 'none', color: '#fff', boxShadow: '0 2px 10px rgba(123,30,60,.30)' }),
  }}>{children}</button>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', padding: '8px 0', borderBottom: `1px solid ${BP.soft}`, fontFamily: BP.font }}>
    <div style={{ width: 160, flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: BP.muted }}>{label}</div>
    <div style={{ fontSize: 13, color: BP.navy, flex: 1 }}>{value ?? '—'}</div>
  </div>
);

const Chip = ({ children, bg, fg, border }) => (
  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999,
    fontSize: 12, fontWeight: 600, fontFamily: BP.font,
    background: bg, color: fg, border: border ? `1px solid ${border}` : 'none',
    marginRight: 6, marginBottom: 6 }}>
    {children}
  </span>
);

// Frosted-glass pill for the hero band (dashboard-hero style)
const HeroChip = ({ icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 13px',
    borderRadius: 999, fontSize: 12.5, fontWeight: 700, fontFamily: BP.font, color: '#fff',
    background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.28)',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', whiteSpace: 'nowrap' }}>
    {icon && <span style={{ fontSize: 13 }}>{icon}</span>}{children}
  </span>
);

const EmptyState = ({ children }) => (
  <div style={{ padding: '14px 12px', borderRadius: 10, background: BP.soft,
    fontSize: 12.5, color: BP.muted, fontFamily: BP.font }}>
    {children}
  </div>
);

const StatusPill = ({ status }) => {
  const s = statusBadge(status);
  return (
    <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: s.background, color: s.color, whiteSpace: 'nowrap' }}>
      {String(status || '—').replace(/_/g, ' ')}
    </span>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────────
const SEVERITY_STYLE = {
  CRITICAL: { bg: COLOR.redBg, fg: COLOR.redDark, border: COLOR.redBorder },
  SEVERE:   { bg: COLOR.redBg, fg: COLOR.redDark, border: COLOR.redBorder },
  WARNING:  { bg: COLOR.amberBg, fg: COLOR.amberDark, border: COLOR.amberBorder },
  INFO:     { bg: COLOR.blueBg, fg: COLOR.blue, border: COLOR.blueBorder },
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
};
const computeAge = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

// Tolerate both raw payloads and { success, data } envelopes from the API layer.
const unwrapEnv = (v) =>
  (v && typeof v === 'object' && !Array.isArray(v) && v.success !== undefined && 'data' in v)
    ? v.data : v;

// Same list as workforce-module/SSNurses.js — keep in sync.
const SPECIALIZATIONS = [
  'WOUND_CARE', 'ELDERLY_CARE', 'PHYSIOTHERAPY', 'INJECTIONS',
  'POST_SURGERY', 'PALLIATIVE_CARE', 'PEDIATRIC', 'PSYCHIATRIC',
  'MATERNAL_CARE', 'REHABILITATION',
];

const AVAILABILITY_STATES = [
  { id: 'AVAILABLE', label: 'Available', icon: '🟢' },
  { id: 'ON_VISIT',  label: 'On visit',  icon: '🩺' },
  { id: 'OFF_DUTY',  label: 'Off duty',  icon: '🌙' },
  { id: 'ON_LEAVE',  label: 'On leave',  icon: '🌴' },
];

const maskId = (v) => {
  const s = String(v || '');
  if (!s) return null;
  if (s.length <= 3) return s;
  return '•'.repeat(Math.max(s.length - 3, 4)) + s.slice(-3);
};

// License expiry → status descriptor (red past, amber within 90 days)
const licenseExpiryStatus = (expiry) => {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return { tone: 'red', label: `Expired ${fmtDate(expiry)}`, days };
  if (days <= 90) return { tone: 'amber', label: `Expires in ${days}d`, days };
  return { tone: 'green', label: `Valid until ${fmtDate(expiry)}`, days };
};

const ExpiryChip = ({ expiry }) => {
  const st = licenseExpiryStatus(expiry);
  if (!st) return <span style={{ color: BP.muted }}>—</span>;
  const tones = {
    red:   { bg: COLOR.redBg, fg: COLOR.redDark, border: COLOR.redBorder, icon: '⛔' },
    amber: { bg: COLOR.amberBg, fg: COLOR.amberDark, border: COLOR.amberBorder, icon: '⚠️' },
    green: { bg: COLOR.greenBg, fg: COLOR.greenDark, border: COLOR.greenBorder, icon: '✓' },
  };
  const t = tones[st.tone];
  return <Chip bg={t.bg} fg={t.fg} border={t.border}>{t.icon} {st.label}</Chip>;
};

const StarRating = ({ rating }) => {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: BP.font }}>
      <span style={{ fontSize: 15, letterSpacing: 1 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <span key={i} style={{ color: i <= Math.round(r) ? '#F6A609' : '#D7DFEA' }}>★</span>
        ))}
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: BP.navy }}>{r.toFixed(1)}</span>
    </span>
  );
};

const StatBox = ({ value, label }) => (
  <div style={{ background: COLOR.blueBg, borderRadius: 10, padding: '10px 16px',
    display: 'inline-flex', alignItems: 'baseline', gap: 8, marginRight: 10, marginBottom: 8 }}>
    <span style={{ fontSize: 22, fontWeight: 800, color: BP.primary }}>{value}</span>
    <span style={{ fontSize: 12.5, fontWeight: 600, color: BP.navy }}>{label}</span>
  </div>
);

const ToggleChip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick} style={{
    padding: '6px 13px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    fontFamily: BP.font, cursor: 'pointer', marginRight: 6, marginBottom: 6,
    transition: 'all .15s',
    background: active ? BP.gradBtn : '#fff',
    color: active ? '#fff' : BP.navy,
    border: active ? '1.5px solid transparent' : `1.5px solid ${BP.border}`,
    boxShadow: active ? '0 2px 8px rgba(123,30,60,.28)' : 'none',
  }}>{children}</button>
);

const NO_PROFILE_HINT =
  'No clinical profile on file yet — it is created by your care team during your first assessment.';
const NO_PRO_PROFILE_HINT =
  'No professional profile on file yet — contact an administrator.';

// ── Patient record sections (top level — see file header note) ───────────────

function FlagsBanner({ flags }) {
  if (!flags || flags.length === 0) return null;
  return (
    <div style={{ background: COLOR.redBg, border: `1.5px solid ${COLOR.redBorder}`,
      borderRadius: 14, padding: '14px 18px', marginBottom: 14, fontFamily: BP.font,
      animation: 'bpFadeUp .35s ease both' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR.redDark, marginBottom: 8,
        display: 'flex', alignItems: 'center', gap: 7 }}>
        <span>⚠️</span>Clinical flags on your record
      </div>
      {flags.map(f => {
        const sev = SEVERITY_STYLE[String(f.severity || '').toUpperCase()] || SEVERITY_STYLE.INFO;
        return (
          <div key={f.flag_id} style={{ display: 'flex', alignItems: 'baseline', gap: 8,
            flexWrap: 'wrap', padding: '5px 0' }}>
            <Chip bg={sev.bg} fg={sev.fg} border={sev.border}>
              {String(f.severity || 'INFO').toUpperCase()}
            </Chip>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: BP.navy }}>
              {String(f.flag_type || '').replace(/_/g, ' ')}
            </span>
            {f.description && <span style={{ fontSize: 12.5, color: BP.navy }}>— {f.description}</span>}
          </div>
        );
      })}
    </div>
  );
}

function MedicalProfileCard({ profile, partyId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [allergies, setAllergies] = useState('');
  const [conditions, setConditions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!profile) {
    return (
      <BPSection icon="🩺" title="Medical profile">
        <EmptyState>{NO_PROFILE_HINT}</EmptyState>
      </BPSection>
    );
  }

  const startEdit = () => {
    setAllergies((profile.allergies || []).join(', '));
    setConditions((profile.chronic_conditions || []).join(', '));
    setError(''); setSuccess(''); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await patientAPI.updateProfile(partyId, {
        allergies: allergies.split(',').map(s => s.trim()).filter(Boolean),
        chronic_conditions: conditions.split(',').map(s => s.trim()).filter(Boolean),
      });
      setSuccess('Medical profile updated.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : e.message);
    } finally {
      setSaving(false);
    }
  };

  const age = computeAge(profile.dob);
  return (
    <BPSection icon="🩺" title="Medical profile"
      action={!editing && partyId != null && (
        <BPButton small ghost onClick={startEdit}>Edit</BPButton>
      )}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      <InfoRow label="Date of birth"
        value={profile.dob ? `${fmtDate(profile.dob)}${age != null ? ` (age ${age})` : ''}` : '—'} />
      <InfoRow label="Gender" value={profile.gender || '—'} />
      <InfoRow label="Blood group" value={profile.blood_group
        ? <Chip bg={COLOR.redBg} fg={COLOR.redDark} border={COLOR.redBorder}>{profile.blood_group}</Chip>
        : '—'} />
      {editing ? (
        <div style={{ maxWidth: 420, padding: '12px 0' }}>
          <BPField label="Allergies (comma-separated)">
            <BPTextInput value={allergies} onChange={e => setAllergies(e.target.value)}
              placeholder="e.g. penicillin, peanuts" />
          </BPField>
          <BPField label="Chronic conditions (comma-separated)">
            <BPTextInput value={conditions} onChange={e => setConditions(e.target.value)}
              placeholder="e.g. hypertension, asthma" />
          </BPField>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : (
        <>
          <InfoRow label="Allergies" value={(profile.allergies && profile.allergies.length)
            ? profile.allergies.map(a => (
                <Chip key={a} bg={COLOR.redBg} fg={COLOR.redDark} border={COLOR.redBorder}>{a}</Chip>))
            : 'None recorded'} />
          <InfoRow label="Chronic conditions" value={(profile.chronic_conditions && profile.chronic_conditions.length)
            ? profile.chronic_conditions.map(c => (
                <Chip key={c} bg={COLOR.amberBg} fg={COLOR.amberDark} border={COLOR.amberBorder}>{c}</Chip>))
            : 'None recorded'} />
        </>
      )}
      <InfoRow label="Medical notes" value={profile.medical_notes || '—'} />
      <div style={{ fontSize: 12, color: BP.muted, marginTop: 10 }}>
        Date of birth, gender, blood group and medical notes are recorded by your care team.
      </div>
    </BPSection>
  );
}

function EmergencyContactCard({ profile, patientId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const startEdit = () => {
    setName(profile?.emergency_contact || '');
    setPhone(profile?.emergency_phone || '');
    setError(''); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await patientAPI.updateProfile(patientId, {
        emergency_contact: name || null, emergency_phone: phone || null,
      });
      setSuccess('Emergency contact updated.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BPSection icon="🚑" title="Emergency contact"
      action={!editing && (
        <BPButton small ghost disabled={!profile} onClick={startEdit}>Edit</BPButton>
      )}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {!profile ? (
        <EmptyState>{NO_PROFILE_HINT}</EmptyState>
      ) : editing ? (
        <div style={{ maxWidth: 380 }}>
          <BPField label="Contact name">
            <BPTextInput value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane Doe" />
          </BPField>
          <BPField label="Contact phone">
            <BPTextInput value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. +49 170 0000000" />
          </BPField>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : (
        <>
          <InfoRow label="Name" value={profile.emergency_contact || '—'} />
          <InfoRow label="Phone" value={profile.emergency_phone || '—'} />
        </>
      )}
    </BPSection>
  );
}

const CONTACT_TYPES = ['HOME', 'WORK', 'MOBILE', 'OTHER'];

const blankContactForm = (c) => ({
  contact_type: c?.contact_type || 'HOME',
  phone: c?.phone || '',
  email: c?.email || '',
  address: c?.address || '',
  city: c?.city || '',
  county_district: c?.county_district || '',
  postal_code: c?.postal_code || '',
  is_primary: !!c?.is_primary,
});

function ContactForm({ form, setForm, saving, onSave, onCancel, showPrimary }) {
  const f = form;
  return (
    <div style={{ maxWidth: 420, paddingTop: 8 }}>
      <BPField label="Contact type">
        <BPSelect value={f.contact_type}
          onChange={e => setForm(v => ({ ...v, contact_type: e.target.value }))}>
          {CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </BPSelect>
      </BPField>
      <BPField label="Phone">
        <BPTextInput value={f.phone}
          onChange={e => setForm(v => ({ ...v, phone: e.target.value }))}
          placeholder="e.g. +254 700 000000" />
      </BPField>
      <BPField label="Email">
        <BPTextInput type="email" value={f.email}
          onChange={e => setForm(v => ({ ...v, email: e.target.value }))}
          placeholder="e.g. you@example.com" />
      </BPField>
      <BPField label="Address">
        <BPTextInput value={f.address}
          onChange={e => setForm(v => ({ ...v, address: e.target.value }))}
          placeholder="Street address" />
      </BPField>
      <BPField label="City">
        <BPTextInput value={f.city}
          onChange={e => setForm(v => ({ ...v, city: e.target.value }))}
          placeholder="e.g. Nairobi" />
      </BPField>
      <BPField label="County / district">
        <BPTextInput value={f.county_district}
          onChange={e => setForm(v => ({ ...v, county_district: e.target.value }))}
          placeholder="e.g. Westlands" />
      </BPField>
      <BPField label="Postal code">
        <BPTextInput value={f.postal_code}
          onChange={e => setForm(v => ({ ...v, postal_code: e.target.value }))}
          placeholder="e.g. 00100" />
      </BPField>
      {showPrimary && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          fontSize: 13, fontWeight: 600, color: BP.navy, cursor: 'pointer', fontFamily: BP.font }}>
          <input type="checkbox" checked={f.is_primary}
            onChange={e => setForm(v => ({ ...v, is_primary: e.target.checked }))}
            style={{ width: 16, height: 16, accentColor: BP.primary }} />
          Make this my primary contact
        </label>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <BPButton small disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</BPButton>
        <BPButton small ghost disabled={saving} onClick={onCancel}>Cancel</BPButton>
      </div>
    </div>
  );
}

function ContactDetailsCard({ contacts, onSaved, editable = true }) {
  // Local override so a mutation's fresh /auth/me payload is reflected
  // immediately; the incoming prop (refetched via onSaved → loadMe) wins
  // whenever it changes.
  const [override, setOverride] = useState(null);
  useEffect(() => { setOverride(null); }, [contacts]);
  const list = override ?? contacts ?? [];
  const sorted = [...list].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null); // contact_id being edited
  const [form, setForm] = useState(blankContactForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);       // set-primary / delete in flight
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applyResult = (me, msg) => {
    const fresh = unwrapEnv(me);
    if (fresh && Array.isArray(fresh.contacts)) setOverride(fresh.contacts);
    setSuccess(msg);
    onSaved?.();
  };

  const fail = (e) => {
    const detail = e.response?.data?.detail;
    setError(typeof detail === 'string' ? detail
      : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
      : e.message);
  };

  const payload = (f) => ({
    contact_type: f.contact_type,
    phone: f.phone || null,
    email: f.email || null,
    address: f.address || null,
    city: f.city || null,
    county_district: f.county_district || null,
    postal_code: f.postal_code || null,
    is_primary: f.is_primary,
  });

  const startAdd = () => {
    setForm(blankContactForm({ is_primary: list.length === 0 }));
    setEditingId(null); setAdding(true); setError(''); setSuccess('');
  };
  const startEdit = (c) => {
    setForm(blankContactForm(c));
    setAdding(false); setEditingId(c.contact_id); setError(''); setSuccess('');
  };
  const cancel = () => { setAdding(false); setEditingId(null); };

  const saveAdd = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      applyResult(await authAPI.addMyContact(payload(form)), 'Contact added.');
      setAdding(false);
    } catch (e) { fail(e); } finally { setSaving(false); }
  };

  const saveEdit = async (c) => {
    setSaving(true); setError(''); setSuccess('');
    try {
      applyResult(await authAPI.updateMyContact(c.contact_id, payload(form)), 'Contact updated.');
      setEditingId(null);
    } catch (e) { fail(e); } finally { setSaving(false); }
  };

  const setPrimary = async (c) => {
    if (busyId) return;
    setBusyId(c.contact_id); setError(''); setSuccess('');
    try {
      applyResult(await authAPI.updateMyContact(c.contact_id, { is_primary: true }),
        'Primary contact updated.');
    } catch (e) { fail(e); } finally { setBusyId(null); }
  };

  const remove = async (c) => {
    if (busyId) return;
    if (!window.confirm('Delete this contact? This cannot be undone.')) return;
    setBusyId(c.contact_id); setError(''); setSuccess('');
    try {
      applyResult(await authAPI.deleteMyContact(c.contact_id), 'Contact deleted.');
    } catch (e) { fail(e); } finally { setBusyId(null); }
  };

  const detailRow = (label, value) => value ? (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0', fontSize: 12.5 }}>
      <span style={{ width: 110, flexShrink: 0, fontWeight: 600, color: BP.muted }}>{label}</span>
      <span style={{ color: BP.navy }}>{value}</span>
    </div>
  ) : null;

  const linkBtn = (label, onClick, danger) => (
    <button type="button" onClick={onClick} disabled={!!busyId}
      style={{ background: 'none', border: 'none', cursor: busyId ? 'wait' : 'pointer',
        padding: 0, fontSize: 12, fontWeight: 700, fontFamily: BP.font,
        color: danger ? BP.danger : BP.primary, opacity: busyId ? 0.5 : 1 }}>
      {label}
    </button>
  );

  const renderContact = (c, i) => {
    const isEditing = editable && editingId === c.contact_id;
    return (
      <div key={c.contact_id || i} style={{ border: `1px solid ${BP.border}`, borderRadius: 12,
        padding: '12px 14px', marginBottom: 10, background: c.is_primary ? COLOR.blueBg : '#fff',
        opacity: busyId && busyId === c.contact_id ? 0.6 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Chip bg={BP.soft} fg={BP.navy} border={BP.border}>
            {String(c.contact_type || 'CONTACT').replace(/_/g, ' ')}
          </Chip>
          {c.is_primary && <Badge color="blue">PRIMARY</Badge>}
          {editable && !isEditing && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12 }}>
              {linkBtn('Edit', () => startEdit(c))}
              {!c.is_primary && linkBtn('Set primary', () => setPrimary(c))}
              {linkBtn('Delete', () => remove(c), true)}
            </span>
          )}
        </div>
        {isEditing ? (
          <ContactForm form={form} setForm={setForm} saving={saving}
            onSave={() => saveEdit(c)} onCancel={cancel} showPrimary={!c.is_primary} />
        ) : (
          <div style={{ marginTop: 6 }}>
            {detailRow('Phone', c.phone)}
            {detailRow('Email', c.email)}
            {detailRow('Address', c.address)}
            {detailRow('City', c.city)}
            {detailRow('County / district', c.county_district)}
            {detailRow('Postal code', c.postal_code)}
            {detailRow('Country', c.country)}
            {!c.phone && !c.email && !c.address && !c.city && !c.county_district && !c.postal_code && (
              <div style={{ fontSize: 12.5, color: BP.muted }}>No details recorded.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <BPSection icon="📞" title="Contact details"
      action={editable && !adding && editingId == null && (
        <BPButton small ghost onClick={startAdd}>+ Add contact</BPButton>
      )}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {adding && (
        <div style={{ border: `1.5px dashed ${BP.border}`, borderRadius: 12,
          padding: '6px 14px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: BP.navy, marginTop: 8 }}>New contact</div>
          <ContactForm form={form} setForm={setForm} saving={saving}
            onSave={saveAdd} onCancel={cancel} showPrimary />
        </div>
      )}
      {sorted.length === 0 ? (
        !adding && (
          <EmptyState>
            No contact details on file{editable ? ' — use the button above to add your primary contact.' : '.'}
          </EmptyState>
        )
      ) : (
        sorted.map(renderContact)
      )}
    </BPSection>
  );
}

const CHANNELS = ['SMS', 'EMAIL', 'CALL', 'PORTAL', 'WHATSAPP'];

function PreferencesCard({ prefs, patientId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const startEdit = () => {
    setForm({
      prefers_female_nurse: !!prefs?.prefers_female_nurse,
      preferred_language: prefs?.preferred_language || '',
      preferred_communication_channel: prefs?.preferred_communication_channel || '',
      special_instructions: prefs?.special_instructions || '',
      dietary_restrictions: (prefs?.dietary_restrictions || []).join(', '),
    });
    setError(''); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await patientAPI.upsertPreferences(patientId, {
        prefers_female_nurse: form.prefers_female_nurse,
        preferred_language: form.preferred_language || null,
        preferred_communication_channel: form.preferred_communication_channel || null,
        special_instructions: form.special_instructions || null,
        dietary_restrictions: form.dietary_restrictions
          .split(',').map(s => s.trim()).filter(Boolean),
      });
      setSuccess('Preferences saved.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BPSection icon="💙" title="My care preferences"
      action={!editing && <BPButton small ghost onClick={startEdit}>{prefs ? 'Edit' : 'Set preferences'}</BPButton>}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {editing && form ? (
        <div style={{ maxWidth: 420 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            fontSize: 13, fontWeight: 600, color: BP.navy, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.prefers_female_nurse}
              onChange={e => setForm(f => ({ ...f, prefers_female_nurse: e.target.checked }))}
              style={{ width: 16, height: 16, accentColor: BP.primary }} />
            I prefer a female nurse
          </label>
          <BPField label="Preferred language">
            <BPTextInput value={form.preferred_language}
              onChange={e => setForm(f => ({ ...f, preferred_language: e.target.value }))}
              placeholder="e.g. English" />
          </BPField>
          <BPField label="Preferred communication channel">
            <BPSelect value={form.preferred_communication_channel}
              onChange={e => setForm(f => ({ ...f, preferred_communication_channel: e.target.value }))}>
              <option value="">— No preference —</option>
              {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
            </BPSelect>
          </BPField>
          <BPField label="Special instructions">
            <BPTextarea value={form.special_instructions}
              onChange={e => setForm(f => ({ ...f, special_instructions: e.target.value }))}
              placeholder="Anything your care team should know" />
          </BPField>
          <BPField label="Dietary restrictions (comma-separated)">
            <BPTextInput value={form.dietary_restrictions}
              onChange={e => setForm(f => ({ ...f, dietary_restrictions: e.target.value }))}
              placeholder="e.g. vegetarian, no nuts" />
          </BPField>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save preferences'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : !prefs ? (
        <EmptyState>No preferences set yet — tell us how you would like to be cared for.</EmptyState>
      ) : (
        <>
          <InfoRow label="Female nurse preferred" value={prefs.prefers_female_nurse ? 'Yes' : 'No'} />
          <InfoRow label="Preferred language" value={prefs.preferred_language || '—'} />
          <InfoRow label="Communication channel" value={prefs.preferred_communication_channel || '—'} />
          <InfoRow label="Special instructions" value={prefs.special_instructions || '—'} />
          <InfoRow label="Dietary restrictions" value={(prefs.dietary_restrictions && prefs.dietary_restrictions.length)
            ? prefs.dietary_restrictions.map(d => (
                <Chip key={d} bg={COLOR.greenBg} fg={COLOR.greenDark} border={COLOR.greenBorder}>{d}</Chip>))
            : '—'} />
        </>
      )}
    </BPSection>
  );
}

function CareSummaryCard({ summary }) {
  if (!summary) {
    return (
      <BPSection icon="📊" title="Care summary">
        <EmptyState>No care activity recorded yet.</EmptyState>
      </BPSection>
    );
  }
  const { recent_visits = [], active_care_plans = [], recent_requests = [], total_visits = 0 } = summary;
  const subhead = (t) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: BP.muted, textTransform: 'uppercase',
      letterSpacing: '0.4px', margin: '0 0 6px' }}>{t}</div>
  );
  return (
    <BPSection icon="📊" title="Care summary">
      <StatBox value={total_visits} label="total visits" />

      {/* three mini-columns on wide screens, stacked on narrow */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginTop: 8 }}>
        <div>
          {subhead('Recent visits')}
          {recent_visits.length === 0 ? <EmptyState>No visits yet.</EmptyState> : recent_visits.map(v => (
            <div key={v.visit_id} style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${BP.soft}` }}>
              <span style={{ fontSize: 13, color: BP.navy }}>{fmtDateTime(v.scheduled_start)}</span>
              <StatusPill status={v.visit_status} />
            </div>
          ))}
        </div>

        <div>
          {subhead('Active care plans')}
          {active_care_plans.length === 0 ? <EmptyState>No active care plans.</EmptyState> : active_care_plans.map(c => (
            <div key={c.care_plan_id} style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${BP.soft}` }}>
              <span style={{ fontSize: 13, color: BP.navy }}>{c.diagnosis || 'Care plan'}</span>
              <StatusPill status={c.status} />
            </div>
          ))}
        </div>

        <div>
          {subhead('Recent requests')}
          {recent_requests.length === 0 ? <EmptyState>No care requests yet.</EmptyState> : recent_requests.map(r => (
            <div key={r.request_id} style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${BP.soft}` }}>
              <span style={{ fontSize: 13, color: BP.navy }}>
                {String(r.service_type || 'Request').replace(/_/g, ' ')}
                <span style={{ color: BP.muted, fontSize: 12 }}> · {fmtDate(r.created_at)}</span>
              </span>
              <StatusPill status={r.status} />
            </div>
          ))}
        </div>
      </div>
    </BPSection>
  );
}

// GDPR Art. 15/20 — one-click subject-access export of the full record.
function DownloadMyDataCard({ partyId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await patientAPI.exportData(partyId);
      const data = resp?.data ?? resp; // strip the {success, data} envelope
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-health-record-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <BPSection icon="📥" title="Download my data">
      <p style={{ fontSize: 12.5, color: BP.muted, margin: '0 0 12px', lineHeight: 1.6 }}>
        Get a copy of everything Benevita holds about you — identity, contacts,
        clinical profile, flags, consents, care requests, care plans, visits,
        prescriptions, observations and invoices — as a single file. This is
        your right under GDPR Article 15 and the Kenya Data Protection Act.
      </p>
      <BPButton onClick={download} disabled={busy}>
        {busy ? 'Preparing…' : 'Download my record (JSON)'}
      </BPButton>
    </BPSection>
  );
}

function ConsentsCard({ consents }) {
  const badge = (c) => {
    if (c.revoked) return <StatusPill status="REVOKED" />;
    if (c.valid_to && new Date(c.valid_to) < new Date()) return <StatusPill status="EXPIRED" />;
    return <StatusPill status="ACTIVE" />;
  };
  return (
    <BPSection icon="✅" title="My consents">
      {(!consents || consents.length === 0) ? (
        <EmptyState>No consent records yet.</EmptyState>
      ) : consents.map(c => (
        <div key={c.consent_id} style={{ padding: '9px 0', borderBottom: `1px solid ${BP.soft}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: BP.navy }}>
              {String(c.consent_type || '').replace(/_/g, ' ')}
            </span>
            {badge(c)}
          </div>
          <div style={{ fontSize: 12, color: BP.muted, marginTop: 3 }}>
            Grantee: {c.grantee_type || '—'} #{String(c.grantee_party_id || '').slice(0, 8)}
            {' · '}Valid {fmtDate(c.valid_from)} → {c.valid_to ? fmtDate(c.valid_to) : 'open-ended'}
          </div>
        </div>
      ))}
    </BPSection>
  );
}

// ── Personal info / org / role-purpose cards (all person roles) ──────────────

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'fr', label: 'French' },
];
const langLabel = (code) => {
  const m = LANGUAGE_OPTIONS.find(l => l.code === code);
  return m ? `${m.label} (${m.code})` : code;
};

function PersonalInfoCard({ me, name, roles, onSaved }) {
  const m = me || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const startEdit = () => {
    setForm({
      display_name: m.display_name || '',
      date_of_birth: m.date_of_birth ? String(m.date_of_birth).slice(0, 10) : '',
      gender_identity: m.gender_identity || '',
      nationality: m.nationality || '',
      preferred_language: m.preferred_language || '',
      national_id: m.national_id || '',
    });
    setError(''); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await authAPI.updateMe({
        display_name: form.display_name || null,
        date_of_birth: form.date_of_birth || null,
        gender_identity: form.gender_identity || null,
        nationality: form.nationality || null,
        preferred_language: form.preferred_language || null,
        national_id: form.national_id || null,
      });
      setSuccess('Personal information updated.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : e.message);
    } finally {
      setSaving(false);
    }
  };

  const age = computeAge(m.date_of_birth);

  return (
    <BPSection icon="👤" title="Personal information"
      action={!editing && <BPButton small ghost onClick={startEdit}>Edit</BPButton>}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {editing && form ? (
        <div style={{ maxWidth: 420 }}>
          <BPField label="Display name">
            <BPTextInput value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="How you'd like to be addressed" />
          </BPField>
          <BPField label="Date of birth">
            <BPTextInput type="date" value={form.date_of_birth}
              onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
          </BPField>
          <BPField label="Gender identity">
            <BPTextInput value={form.gender_identity}
              onChange={e => setForm(f => ({ ...f, gender_identity: e.target.value }))}
              placeholder="e.g. Female" />
          </BPField>
          <BPField label="Nationality">
            <BPTextInput value={form.nationality}
              onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))}
              placeholder="e.g. Kenyan" />
          </BPField>
          <BPField label="Preferred language">
            <BPSelect value={form.preferred_language}
              onChange={e => setForm(f => ({ ...f, preferred_language: e.target.value }))}>
              <option value="">— Not set —</option>
              {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </BPSelect>
          </BPField>
          <BPField label="National ID">
            <BPTextInput value={form.national_id}
              onChange={e => setForm(f => ({ ...f, national_id: e.target.value }))}
              placeholder="e.g. 12345678" />
          </BPField>
          <div style={{ fontSize: 12, color: BP.muted, marginBottom: 12 }}>
            Your legal name is managed by administration and cannot be changed here.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : (
        <>
          <InfoRow label="Full name" value={
            <span title="Managed by administration">
              {m.name || name}
              <span style={{ fontSize: 11, color: BP.muted, marginLeft: 8 }}>· managed by administration</span>
            </span>} />
          <InfoRow label="Display name" value={m.display_name || '—'} />
          <InfoRow label="Date of birth" value={m.date_of_birth
            ? `${fmtDate(m.date_of_birth)}${age != null ? ` (age ${age})` : ''}` : '—'} />
          <InfoRow label="Gender identity" value={m.gender_identity || '—'} />
          <InfoRow label="Nationality" value={m.nationality || '—'} />
          <InfoRow label="Preferred language" value={m.preferred_language ? langLabel(m.preferred_language) : '—'} />
          <InfoRow label="National ID" value={m.national_id ? maskId(m.national_id) : '—'} />
          <InfoRow label="Roles" value={(roles || []).length
            ? roles.map(r => <Chip key={r} bg={COLOR.blueBg} fg={COLOR.blue} border={COLOR.blueBorder}>{r}</Chip>)
            : '—'} />
          {m.status && <InfoRow label="Account status" value={<StatusPill status={m.status} />} />}
          {(m.username || m.email) && <InfoRow label="Username" value={m.username || m.email} />}
        </>
      )}
    </BPSection>
  );
}

const ROLE_PURPOSE = {
  ADMIN:   { icon: '🛡️', title: 'Administrator',
    text: 'You have full platform administration rights — managing parties, workforce, configuration and access across the whole of HHMS.' },
  BILLING: { icon: '💶', title: 'Finance',
    text: 'You manage billing and receivables — invoices, payments and payer relationships generated by completed home-care visits.' },
  QA:      { icon: '📋', title: 'Quality assurance',
    text: 'You monitor compliance and care quality — audits, incident reviews and clinical documentation standards across HHMS.' },
};

function RolePurposeCard({ roles }) {
  const entries = (roles || []).filter(r => ROLE_PURPOSE[r]);
  if (entries.length === 0) return null;
  return (
    <BPSection icon="🎯" title="Your role in HHMS">
      {entries.map(r => {
        const rp = ROLE_PURPOSE[r];
        return (
          <div key={r} style={{ display: 'flex', gap: 12, padding: '8px 0',
            borderBottom: `1px solid ${BP.soft}` }}>
            <span style={{ fontSize: 20 }}>{rp.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: BP.navy }}>{rp.title}</div>
              <div style={{ fontSize: 12.5, color: BP.muted, lineHeight: 1.6, marginTop: 2 }}>{rp.text}</div>
            </div>
          </div>
        );
      })}
    </BPSection>
  );
}

function OrganisationCard({ party, name }) {
  const p = party || {};
  return (
    <BPSection icon="🏢" title="Organisation record">
      <InfoRow label="Organisation name" value={p.name || name} />
      {p.org_type && <InfoRow label="Type" value={String(p.org_type).replace(/_/g, ' ')} />}
      {p.registration_number && <InfoRow label="Registration number" value={p.registration_number} />}
      {p.website && <InfoRow label="Website" value={p.website} />}
      {p.status && <InfoRow label="Status" value={<StatusPill status={p.status} />} />}
      {p.date_created && <InfoRow label="Registered since" value={fmtDate(p.date_created)} />}
      <div style={{ fontSize: 12, color: BP.muted, marginTop: 10 }}>
        Partnership management (contracts, referral channels and service agreements) is coming
        in a later milestone — for changes to your organisation record, contact an administrator.
      </div>
    </BPSection>
  );
}

// ── Nurse professional profile ────────────────────────────────────────────────

function LicenseRows({ profile }) {
  return (
    <>
      <InfoRow label="License number" value={profile.license_number || '—'} />
      <InfoRow label="License country" value={profile.license_country || '—'} />
      <InfoRow label="License expiry" value={<ExpiryChip expiry={profile.license_expiry} />} />
    </>
  );
}

function NurseProfessionalCard({ profile, partyId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [specs, setSpecs] = useState([]);
  const [years, setYears] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const startEdit = () => {
    setSpecs(Array.isArray(profile?.specialization) ? [...profile.specialization] : []);
    setYears(profile?.experience_years != null ? String(profile.experience_years) : '0');
    setError(''); setEditing(true);
  };

  const toggleSpec = (s) =>
    setSpecs(cur => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await nurseAPI.updateProfile(partyId, {
        specialization: specs,
        experience_years: Math.max(0, parseInt(years, 10) || 0),
      });
      setSuccess('Professional profile updated.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <BPSection icon="🩺" title="Professional profile">
        <EmptyState>{NO_PRO_PROFILE_HINT}</EmptyState>
      </BPSection>
    );
  }

  return (
    <BPSection icon="🩺" title="Professional profile"
      action={!editing && <BPButton small ghost onClick={startEdit}>Edit</BPButton>}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {editing ? (
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 6 }}>
            Specialisations
          </div>
          <div style={{ marginBottom: 12 }}>
            {SPECIALIZATIONS.map(s => (
              <ToggleChip key={s} active={specs.includes(s)} onClick={() => toggleSpec(s)}>
                {s.replace(/_/g, ' ')}
              </ToggleChip>
            ))}
          </div>
          <BPField label="Years of experience">
            <BPTextInput type="number" value={years} suffix="yrs"
              onChange={e => setYears(e.target.value)} placeholder="e.g. 5" />
          </BPField>
          <div style={{ fontSize: 12, color: BP.muted, marginBottom: 12 }}>
            License details are verified credentials — to renew or correct them, contact an administrator.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save profile'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 6 }}>
            <StatBox value={<StarRating rating={profile.rating} />} label="rating" />
            <StatBox value={profile.total_visits ?? 0} label="total visits" />
            <StatBox value={profile.experience_years ?? 0} label="years experience" />
          </div>
          <LicenseRows profile={profile} />
          <InfoRow label="Specialisations" value={(profile.specialization && profile.specialization.length)
            ? profile.specialization.map(s => (
                <Chip key={s} bg={COLOR.blueBg} fg={COLOR.blue} border={COLOR.blueBorder}>
                  {String(s).replace(/_/g, ' ')}
                </Chip>))
            : 'None recorded'} />
        </>
      )}
    </BPSection>
  );
}

function NurseAvailabilityCard({ profile, partyId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [radius, setRadius] = useState(
    profile?.service_radius_km != null ? String(Number(profile.service_radius_km)) : ''
  );
  const [savingRadius, setSavingRadius] = useState(false);

  const saveRadius = async () => {
    const km = Number(radius);
    if (!km || km <= 0 || km > 200) { setError('Service radius must be between 1 and 200 km.'); return; }
    setSavingRadius(true); setError(''); setSuccess('');
    try {
      await nurseAPI.updateProfile(partyId, { service_radius_km: km });
      setSuccess(`Service radius updated to ${km} km.`);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSavingRadius(false);
    }
  };

  const setStatus = async (status) => {
    if (saving || status === profile?.availability_status) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await nurseAPI.updateProfile(partyId, { availability_status: status });
      setSuccess(`Status updated to ${status.replace(/_/g, ' ')}.`);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <BPSection icon="📍" title="Availability & coverage">
        <EmptyState>{NO_PRO_PROFILE_HINT}</EmptyState>
      </BPSection>
    );
  }

  return (
    <BPSection icon="📍" title="Availability & coverage">
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      <div style={{ fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 8 }}>
        Current status — tap to change (saved immediately)
      </div>
      {/* Segmented control */}
      <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0, border: `1.5px solid ${BP.border}`,
        borderRadius: 12, overflow: 'hidden', marginBottom: 14, opacity: saving ? 0.6 : 1 }}>
        {AVAILABILITY_STATES.map((s, i) => {
          const active = profile.availability_status === s.id;
          return (
            <button key={s.id} type="button" onClick={() => setStatus(s.id)} disabled={saving}
              style={{ padding: '10px 18px', fontSize: 12.5, fontWeight: 700, fontFamily: BP.font,
                cursor: saving ? 'wait' : 'pointer', border: 'none',
                borderLeft: i > 0 ? `1px solid ${BP.border}` : 'none',
                background: active ? BP.gradBtn : '#fff',
                color: active ? '#fff' : BP.navy, transition: 'all .15s',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>{s.icon}</span>{s.label}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 8 }}>
        Service radius — how far you travel for visits
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="number" min="1" max="200" value={radius}
            onChange={e => setRadius(e.target.value)}
            style={{ width: 130, padding: '10px 44px 10px 14px', borderRadius: 10,
              border: `1px solid ${BP.border}`, fontSize: 13.5, fontFamily: BP.font,
              outline: 'none', color: BP.navy }}
          />
          <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
            fontSize: 12, color: BP.muted, fontWeight: 600 }}>km</span>
        </div>
        <button type="button" onClick={saveRadius}
          disabled={savingRadius || Number(radius) === Number(profile.service_radius_km)}
          style={{ padding: '10px 18px', borderRadius: 10, border: 'none',
            background: BP.gradBtn, color: '#fff', fontWeight: 700, fontSize: 12.5,
            fontFamily: BP.font, cursor: 'pointer',
            opacity: (savingRadius || Number(radius) === Number(profile.service_radius_km)) ? 0.45 : 1 }}>
          {savingRadius ? 'Saving…' : 'Save radius'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: BP.muted, marginTop: 8 }}>
        The dispatch engine only offers you visits within this distance of your location.
      </div>
    </BPSection>
  );
}

const DispatchExplainerCard = () => (
  <BPSection icon="💡" title="How availability is used">
    <div style={{ fontSize: 13, color: BP.navy, lineHeight: 1.65 }}>
      Your availability drives the dispatch engine — set <b>Off duty</b> when unavailable so
      you aren&apos;t matched to new visits. <b>Available</b> puts you in the matching pool for
      care requests inside your service radius; <b>On visit</b> is normally set automatically
      while you are checked in to a visit, and <b>On leave</b> removes you from planning
      until you return.
    </div>
    <div style={{ fontSize: 12, color: BP.muted, marginTop: 10 }}>
      Status changes apply immediately to new dispatch decisions.
    </div>
  </BPSection>
);

// ── Doctor professional profile ───────────────────────────────────────────────

function DoctorProfessionalCard({ profile, partyId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const startEdit = () => {
    setForm({
      specialization: profile?.specialization || '',
      hospital_affiliation: profile?.hospital_affiliation || '',
      years_experience: profile?.years_experience != null ? String(profile.years_experience) : '',
    });
    setError(''); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await doctorAPI.updateProfile(partyId, {
        specialization: form.specialization || null,
        hospital_affiliation: form.hospital_affiliation || null,
        years_experience: form.years_experience === '' ? null
          : Math.max(0, parseInt(form.years_experience, 10) || 0),
      });
      setSuccess('Professional profile updated.');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <BPSection icon="⚕️" title="Professional profile">
        <EmptyState>{NO_PRO_PROFILE_HINT}</EmptyState>
      </BPSection>
    );
  }

  return (
    <BPSection icon="⚕️" title="Professional profile"
      action={!editing && <BPButton small ghost onClick={startEdit}>Edit</BPButton>}>
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      {success && <SuccessBanner onClose={() => setSuccess('')}>{success}</SuccessBanner>}
      {editing && form ? (
        <div style={{ maxWidth: 420 }}>
          <BPField label="Primary specialisation">
            <BPTextInput value={form.specialization}
              onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))}
              placeholder="e.g. Internal Medicine" />
          </BPField>
          <BPField label="Hospital affiliation">
            <BPTextInput value={form.hospital_affiliation}
              onChange={e => setForm(f => ({ ...f, hospital_affiliation: e.target.value }))}
              placeholder="e.g. Nairobi General Hospital" />
          </BPField>
          <BPField label="Years of experience">
            <BPTextInput type="number" value={form.years_experience} suffix="yrs"
              onChange={e => setForm(f => ({ ...f, years_experience: e.target.value }))}
              placeholder="e.g. 12" />
          </BPField>
          <div style={{ fontSize: 12, color: BP.muted, marginBottom: 12 }}>
            License details are verified credentials — to renew or correct them, contact an administrator.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <BPButton small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save profile'}</BPButton>
            <BPButton small ghost disabled={saving} onClick={() => setEditing(false)}>Cancel</BPButton>
          </div>
        </div>
      ) : (
        <>
          <LicenseRows profile={profile} />
          <InfoRow label="Specialisation" value={profile.specialization
            ? <Chip bg={COLOR.blueBg} fg={COLOR.blue} border={COLOR.blueBorder}>{profile.specialization}</Chip>
            : '—'} />
          <InfoRow label="Sub-specialisations" value={(profile.sub_specializations && profile.sub_specializations.length)
            ? profile.sub_specializations.map(s => (
                <Chip key={s} bg={COLOR.greenBg} fg={COLOR.greenDark} border={COLOR.greenBorder}>
                  {String(s).replace(/_/g, ' ')}
                </Chip>))
            : 'None recorded'} />
          <InfoRow label="Hospital affiliation" value={profile.hospital_affiliation || '—'} />
          <InfoRow label="Years of experience" value={profile.years_experience != null
            ? `${profile.years_experience} years` : '—'} />
        </>
      )}
    </BPSection>
  );
}

// ── Account & security (redesigned) ───────────────────────────────────────────

const PW_RULES = [
  { id: 'len',    label: 'At least 8 characters',      test: (p) => p.length >= 8 },
  { id: 'case',   label: 'Upper & lower case letters', test: (p) => /[a-z]/.test(p) && /[A-Z]/.test(p) },
  { id: 'digit',  label: 'At least one number',        test: (p) => /\d/.test(p) },
  { id: 'symbol', label: 'At least one symbol',        test: (p) => /[^A-Za-z0-9]/.test(p) },
];
const STRENGTH_META = [
  { label: 'Too weak', color: '#ECE3E7' },
  { label: 'Weak', color: COLOR.red },
  { label: 'Fair', color: COLOR.amberDark || '#B7791F' },
  { label: 'Good', color: COLOR.blue },
  { label: 'Strong', color: COLOR.greenDark || '#22863A' },
];

function PasswordCard() {
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const score = PW_RULES.reduce((n, r) => n + (r.test(newPw) ? 1 : 0), 0);
  const meta = STRENGTH_META[score];
  const matches = newPw.length > 0 && newPw === confPw;
  const valid = !!curPw && newPw.length >= 8 && matches;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setError(''); setSaving(true);
    try {
      await authAPI.changePassword({ current_password: curPw, new_password: newPw });
      setCurPw(''); setNewPw(''); setConfPw('');
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <BPSection icon="🔑" title="Password">
        <div style={{ textAlign: 'center', padding: '34px 12px', animation: 'bpFadeUp .35s ease both' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
            background: COLOR.greenBg, border: `1.5px solid ${COLOR.greenBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, color: COLOR.greenDark }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: BP.navy }}>Password changed</div>
          <div style={{ fontSize: 12.5, color: BP.muted, marginTop: 4 }}>
            Use your new password the next time you sign in.
          </div>
        </div>
      </BPSection>
    );
  }

  return (
    <BPSection icon="🔑" title="Password">
      {error && <ErrorBanner onClose={() => setError('')}>{error}</ErrorBanner>}
      <form onSubmit={submit} style={{ maxWidth: 400 }}>
        <BPField label="Current password">
          <BPPasswordInput value={curPw} onChange={e => setCurPw(e.target.value)} autoComplete="current-password" />
        </BPField>
        <BPField label="New password">
          <BPPasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" />
        </BPField>

        {/* Strength meter — 4 segments */}
        <div style={{ display: 'flex', gap: 5, margin: '2px 0 6px' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ flex: 1, height: 5, borderRadius: 4, transition: 'background .25s',
              background: newPw && i <= score ? meta.color : '#E9EFF6' }} />
          ))}
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 10,
          color: newPw ? meta.color : BP.muted }}>
          {newPw ? meta.label : 'Strength meter'}
        </div>

        {/* Requirements checklist */}
        <div style={{ background: BP.soft, borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
          {PW_RULES.map(r => {
            const okRule = r.test(newPw);
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12.5, padding: '3px 0',
                color: okRule ? COLOR.greenDark : BP.muted, fontWeight: okRule ? 700 : 500 }}>
                <span style={{ width: 16, textAlign: 'center' }}>{okRule ? '✓' : '○'}</span>
                {r.label}
              </div>
            );
          })}
        </div>

        <BPField label="Confirm new password">
          <BPPasswordInput value={confPw} onChange={e => setConfPw(e.target.value)} autoComplete="new-password" />
        </BPField>
        {confPw.length > 0 && !matches && (
          <div style={{ fontSize: 12, fontWeight: 600, color: COLOR.redDark, margin: '-6px 0 10px' }}>
            Passwords don&apos;t match yet.
          </div>
        )}

        <BPButton type="submit" disabled={!valid || saving}>
          {saving ? 'Saving…' : 'Change password'}
        </BPButton>
      </form>
    </BPSection>
  );
}

// Light user-agent parsing — browser name + OS only, no fingerprinting.
const parseUserAgent = (ua = '') => {
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Microsoft Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  let os = 'Unknown OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return { browser, os };
};

function SessionCard({ name, roles, profile, onSignOut }) {
  const { browser, os } = parseUserAgent(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState('');

  const loadSessions = () =>
    authAPI.sessions()
      // tolerate both the raw list and the {success, data:[...]} envelope
      .then((r) => setSessions(Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : [])))
      .catch(() => setSessions([]));
  useEffect(() => { loadSessions(); }, []); // one fetch per card mount

  const revoke = async (id) => {
    setBusy(id);
    try { await authAPI.revokeSession(id); await loadSessions(); } finally { setBusy(''); }
  };
  const signOutEverywhere = async () => {
    setBusy('all');
    try { await authAPI.logoutAll(); } finally { onSignOut(); }
  };

  const deviceLabel = (s) => {
    if (s.device_type === 'WEB') return 'Web browser';
    if ((s.device_type || '').startsWith('MOBILE')) return 'Mobile app';
    return s.device_type || 'Device';
  };

  return (
    <BPSection icon="🖥️" title="Sessions & devices">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: BP.grad,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
          {bpInitials(name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: BP.navy }}>{name}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
            {(roles || []).map(r => <Badge key={r} color="gray">{r}</Badge>)}
          </div>
        </div>
      </div>
      <InfoRow label="Signed in as" value={profile?.username || profile?.email || name} />
      <InfoRow label="This device" value={`${browser} on ${os}`} />
      {profile?.last_login && <InfoRow label="Last login" value={fmtDateTime(profile.last_login)} />}

      <div style={{ marginTop: 14, borderTop: `1px solid ${BP.soft}`, paddingTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: BP.muted, textTransform: 'uppercase',
          letterSpacing: '0.6px', marginBottom: 6 }}>
          Active sessions ({sessions.length})
        </div>
        {sessions.map(s => (
          <div key={s.session_id} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 0', borderBottom: `1px solid ${BP.soft}` }}>
            <span style={{ fontSize: 16 }}>{(s.device_type || '').startsWith('MOBILE') ? '📱' : '💻'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: BP.navy }}>
                {deviceLabel(s)}
                {s.is_current && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#2BB673',
                    background: 'rgba(43,182,115,0.1)', borderRadius: 999, padding: '2px 8px' }}>
                    THIS SESSION
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: BP.muted }}>
                {s.ip_address || 'unknown IP'} · since {fmtDateTime(s.started_at)}
              </div>
            </div>
            {!s.is_current && (
              <BPButton ghostDanger small disabled={busy === s.session_id}
                onClick={() => revoke(s.session_id)}>
                {busy === s.session_id ? '…' : 'Revoke'}
              </BPButton>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <BPButton ghostDanger onClick={onSignOut}>Sign out</BPButton>
        <BPButton ghostDanger disabled={busy === 'all'} onClick={signOutEverywhere}>
          {busy === 'all' ? 'Signing out…' : 'Sign out everywhere'}
        </BPButton>
      </div>
    </BPSection>
  );
}

const SecurityRow = ({ tone, label, detail, pill }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0',
    borderBottom: `1px solid ${BP.soft}`, fontFamily: BP.font }}>
    <span style={{ width: 9, height: 9, borderRadius: '50%', marginTop: 5, flexShrink: 0,
      background: tone === 'ok' ? '#2BB673' : '#C3CEDB' }} />
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: BP.navy }}>{label}</span>
        {pill && (
          <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
            background: BP.soft, color: BP.muted, border: `1px solid ${BP.border}` }}>{pill}</span>
        )}
        {tone === 'ok' && !pill && <span style={{ color: '#2BB673', fontWeight: 800, fontSize: 12 }}>✓</span>}
      </div>
      {detail && <div style={{ fontSize: 12, color: BP.muted, marginTop: 2 }}>{detail}</div>}
    </div>
  </div>
);

const SecurityStatusCard = () => (
  <BPSection icon="🛡️" title="Security status">
    <SecurityRow tone="ok" label="Password policy enforced"
      detail="Minimum 10 characters with upper & lower case, a digit and a symbol." />
    <SecurityRow tone="ok" label="Brute-force lockout"
      detail="5 failed sign-ins lock the account for 15 minutes." />
    <SecurityRow tone="ok" label="Refresh-token rotation"
      detail="Sign-in tokens are single-use; replayed tokens revoke all sessions." />
    <SecurityRow tone="muted" label="Two-factor authentication" pill="Planned"
      detail="An extra sign-in step will be available in a future release." />
    <SecurityRow tone="ok" label="Session audit logging"
      detail="All access is recorded for HIPAA compliance." />
  </BPSection>
);

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [meError, setMeError] = useState('');

  // Role resolution
  const roles = me?.roles || user?.roles || [];
  const isPatient = roles.includes('PATIENT');
  const isNurse = !isPatient && roles.includes('NURSE');
  const isDoctor = !isPatient && !isNurse && roles.includes('DOCTOR');
  const isAdmin = roles.includes('ADMIN');
  const partyId = me?.party_id ?? user?.party_id;
  // Family/caregiver panel: only the patient viewing their own profile (self,
  // since the page is always scoped to /auth/me) or an ADMIN may manage grants.
  const canManageFamily = (isPatient || isAdmin) && partyId != null;

  // Patient record state (kept as-is)
  const [record, setRecord] = useState(null);       // GET /patients/{id}
  const [summary, setSummary] = useState(null);
  const [flags, setFlags] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [consents, setConsents] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);

  // Role-scoped state (nurse / doctor / party)
  const [party, setParty] = useState(null);         // GET /parties/{id}
  const [nurseRec, setNurseRec] = useState(null);   // GET /nurses/{id}
  const [doctorRec, setDoctorRec] = useState(null); // GET /doctors/{id}
  const [roleLoading, setRoleLoading] = useState(false);

  const [tab, setTab] = useState('');   // resolved against role-based tab list below

  const loadMe = useCallback((alive = { current: true }) => {
    authAPI.me()
      .then(d => { if (alive.current) setMe(unwrapEnv(d) || {}); })
      .catch(e => { if (alive.current) setMeError(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive.current) setMeLoading(false); });
  }, []);

  useEffect(() => {
    const alive = { current: true };
    loadMe(alive);
    return () => { alive.current = false; };
  }, [loadMe]);

  const loadRecord = useCallback((alive = { current: true }) => {
    if (!partyId) return;
    setRecordLoading(true);
    const calls = [
      patientAPI.get(partyId),
      ...(isPatient ? [
        patientAPI.summary(partyId),
        patientAPI.getFlags(partyId, { active_only: true }),
        patientAPI.getPreferences(partyId),
        patientAPI.getConsents(partyId),
      ] : []),
    ];
    Promise.allSettled(calls).then(results => {
      if (!alive.current) return;
      const [rec, sum, flg, prf, cns] = results;
      if (rec.status === 'fulfilled') setRecord(unwrapEnv(rec.value)); else setRecord(null);
      if (isPatient) {
        if (sum?.status === 'fulfilled') setSummary(unwrapEnv(sum.value));
        if (flg?.status === 'fulfilled') {
          const v = unwrapEnv(flg.value);
          setFlags(Array.isArray(v) ? v : []);
        }
        if (prf?.status === 'fulfilled') setPrefs(unwrapEnv(prf.value) || null);
        if (cns?.status === 'fulfilled') {
          const v = unwrapEnv(cns.value);
          setConsents(Array.isArray(v) ? v : []);
        }
      }
      setRecordLoading(false);
    });
  }, [partyId, isPatient]);

  useEffect(() => {
    const alive = { current: true };
    loadRecord(alive);
    return () => { alive.current = false; };
  }, [loadRecord]);

  // Role-scoped fetches — own party record + professional profile.
  // 403/404 tolerated silently (Promise.allSettled): the card is simply skipped.
  const loadRoleData = useCallback((alive = { current: true }) => {
    if (!partyId) return;
    setRoleLoading(true);
    Promise.allSettled([
      partyAPI.get(partyId),
      isNurse ? nurseAPI.get(partyId) : Promise.reject(new Error('skip')),
      isDoctor ? doctorAPI.get(partyId) : Promise.reject(new Error('skip')),
    ]).then(([pty, nrs, doc]) => {
      if (!alive.current) return;
      setParty(pty.status === 'fulfilled' ? unwrapEnv(pty.value) : null);
      setNurseRec(nrs.status === 'fulfilled' ? unwrapEnv(nrs.value) : null);
      setDoctorRec(doc.status === 'fulfilled' ? unwrapEnv(doc.value) : null);
      setRoleLoading(false);
    });
  }, [partyId, isNurse, isDoctor]);

  useEffect(() => {
    const alive = { current: true };
    loadRoleData(alive);
    return () => { alive.current = false; };
  }, [loadRoleData]);

  const profile = me || user || {};
  const name = profile.display_name || profile.name || profile.full_name || user?.name || '—';
  const isOrg = !isPatient && !isNurse && !isDoctor &&
    (roles.includes('ORGANIZATION') || party?.party_type === 'ORG' || profile.party_type === 'ORG');

  const signOut = () => { logout(); navigate('/login'); };

  const clinicalProfile = record?.profile || null;
  const nurseProfile = nurseRec?.profile || null;
  const doctorProfile = doctorRec?.profile || null;
  const contacts = (me?.contacts?.length ? me.contacts
    : party?.contacts?.length ? party.contacts
    : record?.contacts) || [];

  // ── Tabs per role ───────────────────────────────────────────────────────────
  const tabs = isPatient
    ? [
        { id: 'health', label: '🩺 Health record' },
        { id: 'prefs', label: '💙 Care preferences' },
        { id: 'security', label: '🔐 Account & security' },
      ]
    : isNurse
      ? [
          { id: 'pro', label: '🩺 Professional profile' },
          { id: 'avail', label: '📍 Availability & coverage' },
          { id: 'security', label: '🔐 Account & security' },
        ]
      : isDoctor
        ? [
            { id: 'pro', label: '⚕️ Professional profile' },
            { id: 'security', label: '🔐 Account & security' },
          ]
        : isOrg
          ? [
              { id: 'org', label: '🏢 Organisation' },
              { id: 'security', label: '🔐 Account & security' },
            ]
          : [
              { id: 'overview', label: '👤 Overview' },
              { id: 'security', label: '🔐 Account & security' },
            ];
  const activeTab = tabs.some(t => t.id === tab) ? tab : tabs[0].id;

  // Hero chips
  const heroAge = computeAge(clinicalProfile?.dob);
  const allergyCount = clinicalProfile?.allergies?.length || 0;
  const flagCount = (flags || []).length;
  const nurseLicYear = nurseProfile?.license_expiry ? new Date(nurseProfile.license_expiry).getFullYear() : null;
  const docLicYear = doctorProfile?.license_expiry ? new Date(doctorProfile.license_expiry).getFullYear() : null;

  const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 20, alignItems: 'start' };

  // ── Tab panels ──────────────────────────────────────────────────────────────
  const securityTab = (
    <div style={grid2}>
      <PasswordCard />
      <div>
        <SessionCard name={name} roles={roles} profile={profile} onSignOut={signOut} />
        <SecurityStatusCard />
      </div>
    </div>
  );

  const healthTab = recordLoading && !record ? (
    <div style={{ padding: 24 }}><Spinner label="Loading your patient record…" /></div>
  ) : (
    <>
      <div style={grid2}>
        {/* Col A */}
        <div>
          <MedicalProfileCard profile={clinicalProfile} partyId={partyId} onSaved={() => loadRecord()} />
          <EmergencyContactCard profile={clinicalProfile} patientId={partyId} onSaved={() => loadRecord()} />
        </div>
        {/* Col B */}
        <div>
          <PersonalInfoCard me={profile} name={name} roles={roles} onSaved={() => loadMe()} />
          <ContactDetailsCard contacts={contacts} onSaved={() => loadMe()} />
          <ConsentsCard consents={consents} />
          <FamilyAccess patientId={partyId} canManage={canManageFamily} />
          <DownloadMyDataCard partyId={partyId} />
        </div>
      </div>
      {/* Full-width care summary */}
      <CareSummaryCard summary={summary} />
    </>
  );

  const prefsTab = (
    <div style={grid2}>
      <div style={{ maxWidth: 720 }}>
        <PreferencesCard prefs={prefs} patientId={partyId} onSaved={() => loadRecord()} />
      </div>
      <BPSection icon="💡" title="Why we ask">
        <div style={{ fontSize: 13, color: BP.navy, lineHeight: 1.65 }}>
          Your care preferences help us match you with the right nurse and reach you the way
          you prefer. Language and nurse-gender preferences guide scheduling, dietary
          restrictions are shared with your visiting care team, and your preferred channel is
          used for visit reminders and updates.
        </div>
        <div style={{ fontSize: 12, color: BP.muted, marginTop: 10 }}>
          You can change these at any time — updates apply to future visits.
        </div>
      </BPSection>
    </div>
  );

  const nurseProTab = roleLoading && !nurseRec ? (
    <div style={{ padding: 24 }}><Spinner label="Loading your professional profile…" /></div>
  ) : (
    <div style={grid2}>
      <NurseProfessionalCard profile={nurseProfile} partyId={partyId} onSaved={() => loadRoleData()} />
      <div>
        <PersonalInfoCard me={profile} name={name} roles={roles} onSaved={() => loadMe()} />
        <ContactDetailsCard contacts={contacts} onSaved={() => loadMe()} />
      </div>
    </div>
  );

  const nurseAvailTab = roleLoading && !nurseRec ? (
    <div style={{ padding: 24 }}><Spinner label="Loading your availability…" /></div>
  ) : (
    <div style={grid2}>
      <NurseAvailabilityCard profile={nurseProfile} partyId={partyId} onSaved={() => loadRoleData()} />
      <DispatchExplainerCard />
    </div>
  );

  const doctorProTab = roleLoading && !doctorRec ? (
    <div style={{ padding: 24 }}><Spinner label="Loading your professional profile…" /></div>
  ) : (
    <div style={grid2}>
      <DoctorProfessionalCard profile={doctorProfile} partyId={partyId} onSaved={() => loadRoleData()} />
      <div>
        <PersonalInfoCard me={profile} name={name} roles={roles} onSaved={() => loadMe()} />
        <ContactDetailsCard contacts={contacts} onSaved={() => loadMe()} />
      </div>
    </div>
  );

  const orgTab = (
    <div style={grid2}>
      <OrganisationCard party={party} name={name} />
      <ContactDetailsCard contacts={contacts} editable={false} />
    </div>
  );

  const overviewTab = (
    <div style={grid2}>
      <div>
        {meLoading ? (
          <BPSection icon="👤" title="Personal information">
            <Spinner label="Loading profile…" />
          </BPSection>
        ) : (
          <>
            {meError && <ErrorBanner>{meError}</ErrorBanner>}
            <PersonalInfoCard me={profile} name={name} roles={roles} onSaved={() => loadMe()} />
          </>
        )}
        <RolePurposeCard roles={roles} />
      </div>
      <ContactDetailsCard contacts={contacts} onSaved={() => loadMe()} />
    </div>
  );

  return (
    <div style={{ width: '100%', fontFamily: BP.font }}>
      <style>{'@keyframes bpFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }'}</style>

      {/* Hero band */}
      <div style={{ background: BP.grad, borderRadius: 20, padding: '26px 28px', color: '#fff',
        display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16, flexWrap: 'wrap',
        animation: 'bpFadeUp .35s ease both' }}>
        <BPAvatar name={name} size={64} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.3px' }}>{name}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
            {roles.map(r => <Badge key={r} color="blue">{r}</Badge>)}
          </div>
          {partyId != null && (
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>Party #{partyId}</div>
          )}
        </div>
        {isPatient && clinicalProfile && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {clinicalProfile.blood_group && <HeroChip icon="🩸">{clinicalProfile.blood_group}</HeroChip>}
            {heroAge != null && <HeroChip>Age {heroAge}</HeroChip>}
            <HeroChip>{allergyCount} {allergyCount === 1 ? 'allergy' : 'allergies'}</HeroChip>
            <HeroChip>{flagCount} active {flagCount === 1 ? 'flag' : 'flags'}</HeroChip>
          </div>
        )}
        {isNurse && nurseProfile && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <HeroChip icon={AVAILABILITY_STATES.find(s => s.id === nurseProfile.availability_status)?.icon || '🟢'}>
              {String(nurseProfile.availability_status || '—').replace(/_/g, ' ')}
            </HeroChip>
            <HeroChip icon="★">{(Number(nurseProfile.rating) || 0).toFixed(1)}</HeroChip>
            <HeroChip>{nurseProfile.total_visits ?? 0} visits</HeroChip>
            {nurseLicYear && <HeroChip icon="🪪">License exp. {nurseLicYear}</HeroChip>}
          </div>
        )}
        {isDoctor && doctorProfile && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {doctorProfile.specialization && <HeroChip icon="⚕️">{doctorProfile.specialization}</HeroChip>}
            {doctorProfile.hospital_affiliation && <HeroChip icon="🏥">{doctorProfile.hospital_affiliation}</HeroChip>}
            {docLicYear && <HeroChip icon="🪪">License exp. {docLicYear}</HeroChip>}
          </div>
        )}
      </div>

      {/* Clinical flags warning — always visible, directly under hero */}
      {isPatient && <FlagsBanner flags={flags} />}

      <TabBar tabs={tabs} active={activeTab} onChange={setTab} style={{ marginBottom: 18 }} />

      {isPatient ? (
        activeTab === 'health' ? healthTab : activeTab === 'prefs' ? prefsTab : securityTab
      ) : isNurse ? (
        activeTab === 'pro' ? nurseProTab : activeTab === 'avail' ? nurseAvailTab : securityTab
      ) : isDoctor ? (
        activeTab === 'pro' ? doctorProTab : securityTab
      ) : isOrg ? (
        activeTab === 'org' ? orgTab : securityTab
      ) : (
        activeTab === 'overview' ? overviewTab : securityTab
      )}
    </div>
  );
}
